import { useState } from "react";
import { visibleBody } from "../api/redaction";
import type { Report } from "../api/types";
import { useMe, useServices } from "../app/services";
import { useResource } from "../app/useResource";
import { ConfirmAction } from "../components/cards";
import { Icon } from "../components/Icon";
import { EmptyState, PageHeader, Pill, ResourceView } from "../components/ui";

/**
 * 通報の確認（管理者だけ）。対象・理由・対応状態を見て、投稿を非表示にできる。
 * 他人の非公開メモやお気に入りは、ここからは見られない（仕様書「公開と削除」）
 */
export function ModerationPage() {
  const me = useMe();
  const { api } = useServices();
  const res = useResource("reports.list", () => api.getReports(), [], ["posts"]);
  if (!me?.can_moderate) {
    return (
      <div className="page">
        <PageHeader title="通報の確認" />
        <EmptyState title="管理者だけが見られる画面です" icon="flag" />
      </div>
    );
  }
  return (
    <div className="page">
      <PageHeader title="通報の確認" lead="管理者だけに見えています。投稿者に通報者は伝わりません。" />
      <ResourceView resource={res} what="通報">
        {(reports) =>
          reports.length ? (
            <ul className="report-list">
              {reports.map((r) => (
                <ReportRow key={r.id} report={r} onDone={res.reload} />
              ))}
            </ul>
          ) : (
            <EmptyState title="対応が必要な通報はありません" icon="check" />
          )
        }
      </ResourceView>
    </div>
  );
}

function ReportRow({ report: r, onDone }: { report: Report; onDone: () => void }) {
  const { api } = useServices();
  const [confirm, setConfirm] = useState(false);
  const body = visibleBody(r.post);
  // 対応すると status は resolved になる（backend catalog.moderate）。本文が引けなければ非表示か削除
  const resolved = r.status === "resolved";
  const hidden = resolved && (!r.post || r.post.unavailable);
  return (
    <li className="report">
      <p className="report__head">
        <Pill tone={resolved ? "calm" : "warn"}>{hidden ? "対応済み（非表示）" : resolved ? "対応済み" : "未対応"}</Pill>
      </p>
      <p className="report__reason">
        <strong>理由：</strong>
        {r.reason}
      </p>
      <div className="report__target">
        <p className="report__label">対象の投稿{r.post ? `（${r.post.author_name} さん）` : ""}</p>
        {body !== null ? (
          <p className="report__body">{body || "（ひとことなし）"}</p>
        ) : (
          <p className="muted">本文は表示されていません（削除・非表示）。</p>
        )}
      </div>
      {!resolved && (
        <button type="button" className="btn btn-secondary btn-small" onClick={() => setConfirm(true)}>
          <Icon name="eyeOff" className="btn-icon" />
          投稿を非表示にする
        </button>
      )}
      <ConfirmAction
        open={confirm}
        title="この投稿を非表示にしますか？"
        body="メンバーに本文が見えなくなります。歌唱・キュー・ほかの人の保存は残ります。"
        confirmLabel="非表示にする"
        op="reports.moderate"
        action={() => api.moderateReport(r.id, true)}
        onClose={() => setConfirm(false)}
        onDone={onDone}
      />
    </li>
  );
}
